













































































































































import Vue from 'vue';
import { SignatureService } from '@/services/SignatureService';
import {
  BaseDocumentSearchModel,
  SignatureTemplateModel,
  SignatureCreateModel,
  DocumentSearchModel,
  SignatureSettingModel
} from '@/types';
import { mapActions } from 'vuex';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import WorkflowUnsupportedInput from '@/components/workflow/WorkflowUnsupportedInput.vue';
import { DeviceHelper, SignaturePadHelpers, AppSettingsHelper } from '@/mixins';
import { ObjectUtils } from '@/utils/objectUtils';

export default Vue.extend({
  name: 'WorkflowSignaturePad',
  mixins: [SignaturePadHelpers, DeviceHelper, AppSettingsHelper],
  props: {
    documentSearchModel: {
      type: Object as () => BaseDocumentSearchModel,
      required: true
    },
    inputProperties: {
      type: Object,
      required: true
    },
    data: {
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    isWorkflowJobLocked: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      signatureTemplateModel: {} as SignatureTemplateModel,
      localDocumentSearchModel: {
        documentId: this.documentSearchModel.documentId,
        cabinetId: this.documentSearchModel.cabinetId,
        documentTypeId: 0,
        page: 1
      } as DocumentSearchModel,
      getIsLoading: false,
      postIsLoading: false,
      isValid: false,
      signatureSettingId: 0,
      showExternalPadAlert: false,
      usingExternalPad: false,
      processingExternalPad: false,
      externalPadErrored: false
    };
  },
  methods: {
    ...mapActions('documentViewStore', ['loadPage', 'setDocumentSearchModel']),
    async getData() {
      this.getIsLoading = true;
      this.signatureTemplateModel = await SignatureService.getSignatureTemplateAsync(
        this.localDocumentSearchModel.cabinetId,
        this.localDocumentSearchModel.documentId
      );
      this.getIsLoading = false;
    },
    async processSignaturePad(this: any) {
      const { isEmpty, data } = this.$refs[this.getSignaturePadRef(this.signatureSettingId)][0].saveSignature();
      if (!isEmpty) {
        await this.postData(data);
      }
    },
    async postData(this: any, signatureBase64: string) {
      this.postIsLoading = true;
      await SignatureService.postSignatureAsync(
        this.localDocumentSearchModel.cabinetId,
        this.localDocumentSearchModel.documentId,
        {
          signatureSettingId: this.signatureSettingId,
          signatureBase64
        } as SignatureCreateModel
      )
        .then((result) => {
          this.localDocumentSearchModel = result;
          this.setDocumentSearchModel(this.localDocumentSearchModel);
          this.loadPage(this.localDocumentSearchModel.page);
          this.closeModal(this.signatureSettingId);
          this.postIsLoading = false;
          this.getData();
        })
        .catch((error) => {
          this.postIsLoading = false;
        })
        .finally(() => {
          this.processingExternalPad = false;
        });
    },
    undoSignature(this: any, signatureSettingId: number) {
      this.$refs[this.getSignaturePadRef(signatureSettingId)][0].undoSignature();
      if (this.$refs[this.getSignaturePadRef(signatureSettingId)][0].isEmpty()) {
        this.isValid = false;
      }
    },
    closeModal(this: any, signatureSettingId: number) {
      this.$refs[this.getModalId(signatureSettingId)][0]?.hide();
      this.$refs[this.getSignaturePadRef(this.signatureSettingId)][0]?.clearSignature();
      this.isValid = false;
    },
    setupSignaturePad(this: any, signatureSettingId: number) {
      this.$refs[this.getSignaturePadRef(signatureSettingId)][0].resizeCanvas();
    },
    getModalId(signatureSettingId: number): string {
      return `signature-setting-${signatureSettingId}-modal`;
    },
    getSignaturePadRef(signatureSettingId: number): string {
      return `signature-pad-${signatureSettingId}`;
    },
    getInputValue(isSigned: boolean): string {
      return isSigned ? 'Signed' : 'Unsigned';
    },
    getInputCssClass(isSigned: boolean): string {
      return isSigned ? 'text-success' : 'text-danger';
    },
    getInputGroupButtonVariant(isSigned: boolean): string {
      return isSigned ? 'success' : 'danger';
    },
    openSignatureModal(this: any, signatureSettingId: number) {
      this.showExternalPadAlert = false;
      this.usingExternalPad = false;
      this.signatureSettingId = signatureSettingId;
      const signatureSetting = this.signatureTemplateModel.signatureSettings.find(
        (x: SignatureSettingModel) => x.id === signatureSettingId
      ) as SignatureSettingModel;

      this.loadPage(signatureSetting.pageNumber);

      if (!signatureSetting.isSigned) {
        this.$refs[this.getModalId(signatureSettingId)][0].show();
      }
    },
    loadPageWithSignature(this: any, signatureSettingId: number) {
      const signatureSetting = this.signatureTemplateModel.signatureSettings.find(
        (x: SignatureSettingModel) => x.id === signatureSettingId
      ) as SignatureSettingModel;
      this.loadPage(signatureSetting.pageNumber);
    },
    onEnd(this: any) {
      this.isValid = true;
    },
    async processExternalPadSignature(this: any, signaturePadResponse: any) {
      this.externalPadErrored = signaturePadResponse.errorMsg as boolean;
      if (!this.externalPadErrored) {
        this.processingExternalPad = true;
        await this.postData(`data:image/png;base64,${String(signaturePadResponse.imageData)}`);
      }
    },
    useExternalPad(this: any, height: number, width: number) {
      this.isValid = false;
      this.externalPadErrored = false;
      this.showExternalPadAlert = true;
      this.usingExternalPad = true;
      this.startSignaturePad(this.processExternalPadSignature, height, width);
    },
    externalPadAlertDismissed(this: any) {
      this.showExternalPadAlert = false;
    }
  },
  computed: {
    unsupportedMessage(this: any) {
      if (ObjectUtils.isNullOrEmpty(this.signatureTemplateModel)) {
        return null;
      }
      return !this.signatureTemplateModel.canSign ? 'You do not have permission to sign this document' : null;
    }
  },
  async mounted(this: any) {
    this.getData();
  },
  components: {
    LoadingSpinner,
    WorkflowUnsupportedInput
  }
});
